<script setup lang="ts">
import { TETopLabelLayout, TETextInput, TEButton } from '@ben/vite-te'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

const emit = defineEmits<{
  (event: 'updated', payload: EmitObject): void
}>()
const accessToken =
  'pk.eyJ1IjoiYmVucm8iLCJhIjoiY2x1c3FycTVlMHMxcDJqcGt1NmxkbWU2aSJ9.PoN1Lr0FFMW8KZwPkIBH7Q'
mapboxgl.accessToken = accessToken

const a = ref<mapboxgl.Map | undefined>()
const marker = ref<mapboxgl.Marker | undefined>()
onMounted(() => {
  a.value = new mapboxgl.Map({
    container: 'map', // container ID
    style: 'mapbox://styles/mapbox/streets-v12', // style URL
    center: [11, 51],
    zoom: 4.5, // starting zoom
  })
})

async function lookup() {
  isLoading.value = true
  if (city.value.length > 0 && location_name.value.length > 0) {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${city.value} ${location_name.value}.json?access_token=${accessToken}`,
    )
    const fc = await response.json()
    console.log(fc)
    if (fc.features?.length > 0 && a.value != null) {
      const ft = fc.features[0]
      // Create a new marker.
      marker.value?.remove()
      marker.value = new mapboxgl.Marker({ draggable: true })
        .setLngLat(ft.geometry.coordinates)
        .addTo(a.value)
      a.value.flyTo({ center: ft.geometry.coordinates, zoom: 14, essential: true })
      didLookup.value = true
    }
  } else {
    alert('Fehler bei der Ortssuche, bitte erneut probieren.')
  }
  isLoading.value = false
}

const hasDataToEmit = computed(function () {
  return city.value.length > 0 && location_name.value.length > 0 && marker.value?.getLngLat()
})

const city = ref('')
const location_name = ref('')

interface EmitObject {
  city: string
  location_name: string
  location: mapboxgl.LngLat
}
const isLoading = ref(false)
const didLookup = ref(false)
const didEmit = ref(false)
function emitValue() {
  if (!marker.value?.getLngLat()) {
    console.log('Kein marker')
  }
  const location = marker.value?.getLngLat()
  marker.value?.setDraggable(false)

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  emit('updated', { city: city.value, location_name: location_name.value, location: location! })
  didEmit.value = true
}

const url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/.json?access_token='
</script>

<template>
  <div class="w-full">
    <div class="absolute z-50 m-4 flex w-80 gap-4 rounded bg-gray-50 p-4 shadow dark:bg-gray-800">
      <div class="flex flex-wrap">
        <template v-if="!didLookup">
          <div class="flex gap-2">
            <TETopLabelLayout class="w-full text-white md:w-1/2" title="Stadt" name="city">
              <TETextInput v-model="city" name="city" />
            </TETopLabelLayout>

            <TETopLabelLayout
              class="w-full text-white md:w-1/2"
              title="Startpunkt"
              name="startpoint"
            >
              <TETextInput v-model="location_name" name="startpoint" />
            </TETopLabelLayout>
          </div>

          <TEButton
            id="location_lookup_btn"
            color="purple"
            :disabled="isLoading || city?.length < 1 || location_name?.length < 1"
            @click="lookup"
          >
            <svg
              v-if="isLoading"
              aria-hidden="true"
              class="inline-block size-4 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />

              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            Ort suchen</TEButton
          >
        </template>

        <template v-else-if="!didEmit">
          <div
            class="mb-4 rounded-lg bg-blue-50 p-4 text-sm text-blue-800 dark:bg-gray-800 dark:text-blue-400"
            role="alert"
          >
            <span class="font-medium">Position passt noch nicht?</span> Du kannst den Marker ganz
            einfach auf der Karte verschieben. Wenn es passt klicke einfach auf Ort festlegen.
          </div>

          <TEButton
            id="accept_location"
            :disabled="!hasDataToEmit || didEmit"
            color="purple"
            @click="emitValue"
            >Ort festlegen</TEButton
          >
        </template>

        <template />
      </div>
    </div>

    <div id="map" class="mb-6 h-[75vh] w-full md:h-[50vh]" />
  </div>
</template>
